// @flow

import React, { useState, useEffect, useCallback } from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { useForm } from 'react-hook-form';
import { __ } from '../../../i18n/l10n';
import { useDispatch, useSelector } from 'react-redux';

import {
  WS_CALL_PENDING,
  WS_CLEAR_PENDING,
  WS_UPDATE_USER_REGISTRATION_REQUEST,
} from '../../../store/actions';
import type { RootState, UserUpdate } from '../../../store/types';
import { API } from '../../../utils/APIUtils';
import { submitFormOnEnter } from '../../../utils/CommonUtils';
import { colors } from '../../../styles/GlobalStyle';
import { Button } from './Button';
import { InputText } from './InputText';
import { InputPlaces } from './InputPlaces';
import { InputCountry } from './InputCountry';
import { useCheckForm } from '../hooks/useCheckForm';
import { FormAddressSchema } from './FormAddress.validation';

type FormAddressProps = {|
  className?: string,
  onValid: Function,
  labelButton: string,
|};

type FormAddressData = {|
  address1: string,
  address2: string,
  zip_code?: string,
  city: string,
  country: string,
|};

const schema = FormAddressSchema();

const RawFormAddress = ({ className, onValid, labelButton }: FormAddressProps) => {
  const dispatch = useDispatch();
  const userRegistration = useSelector((state: RootState) => state.userRegistration);
  const common = useSelector((state: RootState) => state.common);
  const websocket = useSelector((state: RootState) => state.websocket);
  const [formValid, setFormValid] = useState(false);
  const [countryInputValue, setCountryInputValue] = useState('');
  const [defaultPostcodeValue, setDefaultPostcodeValue] = useState('');
  const [defaultCityValue, setDefaultCityValue] = useState('');
  const [defaultAddressValue, setDefaultAddressValue] = useState('');

  const getValueFromCommon = useCallback(
    (key: string): string => {
      if (
        userRegistration.user &&
        userRegistration.user[key] &&
        userRegistration.user[key] !== 'Invalid date'
      )
        return userRegistration.user[key];
      if (common.identityScan && common.identityScan[key]) return common.identityScan[key];
      return '';
    },
    [common.identityScan, userRegistration.user]
  );

  const [countryCode, setCountryCode] = useState(getValueFromCommon('country_code') || '');

  const {
    register,
    handleSubmit,
    errors,
    setError,
    getValues,
    setValue,
    clearError,
    watch,
    triggerValidation,
    formState,
  } = useForm({
    validationSchema: schema,
    reValidateMode: 'onChange',
    submitFocusError: false,
    defaultValues: {
      address1: getValueFromCommon('address1'),
      address2: getValueFromCommon('address2'),
      zip_code: getValueFromCommon('zip_code'),
      city: getValueFromCommon('city'),
      country: getValueFromCommon('country'),
    },
  });

  useEffect(() => {
    watch();
  }, [watch]);

  useEffect(() => {
    clearError();
  }, [clearError]);

  const handleKeyDown = useCallback((e, cb) => {
    submitFormOnEnter(e, cb);
  }, []);

  const [checkForm] = useCheckForm(
    getValues,
    schema,
    setFormValid,
    userRegistration.updateUserRegistration.error,
    setError
  );

  // Websocket updates
  useEffect(() => {
    if (userRegistration.updateUserRegistration.response) {
      dispatch({ type: WS_CLEAR_PENDING });
      onValid();
    }
  }, [dispatch, onValid, userRegistration.updateUserRegistration]);

  const onSubmit = useCallback(
    (data: FormAddressData) => {
      const modifiedData: UserUpdate = { ...data };
      modifiedData.country = countryCode;

      dispatch({
        type: WS_CALL_PENDING,
      });

      dispatch({
        type: WS_UPDATE_USER_REGISTRATION_REQUEST,
        payload: {
          method: API.UpdateUserRegistration,
          params: { jwt: common.jwt ? common.jwt : '', fields: modifiedData },
        },
      });
    },
    [common.jwt, dispatch, countryCode]
  );

  useEffect(() => {
    register({ name: 'country' });
    register({ name: 'city' });
    register({ name: 'zip_code' });
    register({ name: 'address1' });
  }, []);

  // Manage links between fields values
  const onAddressChange = useCallback(
    (originField, addressData) => {
      if (
        originField !== 'address1' &&
        originField !== 'zip_code' &&
        !getValues('address1') &&
        addressData.locale_names &&
        addressData.locale_names[0]
      ) {
        setValue('address1', addressData.locale_names[0]);
        clearError('address1');
      }
      if (originField !== 'zip_code' && addressData.postcode && addressData.postcode[0]) {
        setValue('zip_code', addressData.postcode[0]);
        setDefaultPostcodeValue(addressData.postcode[0]);
        clearError('zip_code');
      }
      // TODO: implement conditions for API to return city data in city field and remove these conditions
      if (
        originField !== 'city' &&
        ((addressData.city && addressData.city[0]) ||
          (addressData.locale_names && addressData.locale_names[0]))
      ) {
        setValue(
          'city',
          (addressData.city && addressData.city[0]) ||
            (addressData.locale_names && addressData.locale_names[0])
        );
        setDefaultCityValue(
          (addressData.city && addressData.city[0]) ||
            (addressData.locale_names && addressData.locale_names[0])
        );
        clearError('city');
      }
      if (originField !== 'country' && addressData.country) {
        setValue('country', addressData.country);
        setCountryInputValue(addressData.country);
        clearError('country');

        setCountryCode(addressData.countryCode);
      }
    },
    [clearError, setValue]
  );

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      id="formAddress"
      className={className}
      onKeyDown={(e) => {
        handleKeyDown(e, handleSubmit(onSubmit));
      }}
    >
      <InputPlaces
        value={getValues().address1}
        name="address1"
        queryType="address"
        id="address1"
        label={__('Postal address')}
        ref={register}
        invalid={errors.address1}
        onChange={checkForm}
        type="address"
        onPlaceChange={(data) => {
          setValue('address1', (data.locale_names && data.locale_names[0]) || data || '');
          setDefaultAddressValue('');
          onAddressChange('address1', data);
          if (formState.isSubmitted) {
            triggerValidation('address1');
          }
        }}
        defaultValue={defaultAddressValue || getValues().address1}
      />
      {errors.address1 && <div className="error">{errors.address1.message}</div>}

      <InputText
        value={getValues().address2}
        name="address2"
        id="address2"
        label={__('Address details')}
        ref={register}
        invalid={errors.address2}
        onChange={checkForm}
        optional
      />
      {errors.address2 && <div className="error">{errors.address2.message}</div>}

      <InputPlaces
        value={getValues().zip_code}
        name="zip_code"
        queryType="postcode"
        id="zip_code"
        label={__('Postal code')}
        ref={register}
        invalid={errors.zip_code}
        onChange={checkForm}
        type="zip"
        onPlaceChange={(data) => {
          setValue('zip_code', (data.postcode && data.postcode[0]) || data || '');
          setDefaultPostcodeValue('');
          onAddressChange('zip_code', data);
          if (formState.isSubmitted) {
            triggerValidation('zip_code');
          }
        }}
        defaultValue={defaultPostcodeValue || getValues().zip_code}
      />
      {errors.zip_code && <div className="error">{errors.zip_code.message}</div>}

      <InputPlaces
        value={getValues().city}
        name="city"
        queryType="city"
        id="city"
        label={__('City')}
        ref={register}
        invalid={errors.city}
        onChange={checkForm}
        type="city"
        onPlaceChange={(data) => {
          setValue('city', (data.locale_names && data.locale_names[0]) || data || '');
          setDefaultCityValue('');
          onAddressChange('city', data);
          if (formState.isSubmitted) {
            triggerValidation('city');
          }
        }}
        defaultValue={defaultCityValue || getValues().city}
      />
      {errors.city && <div className="error">{errors.city.message}</div>}

      <InputCountry
        value={getValues().country}
        name="country"
        id="country"
        label={__('Country')}
        ref={register}
        invalid={errors.country}
        onChange={(code) => {
          setValue('country', code);
          setCountryInputValue('');
          checkForm();
          if (formState.isSubmitted) {
            triggerValidation('country');
          }
        }}
        setCountryCode={setCountryCode}
        defaultCountryValue={countryInputValue || getValues().country}
      />
      {errors.country && <div className="error">{errors.country.message}</div>}

      <Button type="submit" picto="validate" isDisabled={!formValid || websocket.pendingCall}>
        {labelButton}
      </Button>
    </form>
  );
};

export const FormAddress: StyledComponent<FormAddressProps, {}, typeof RawFormAddress> = styled(
  RawFormAddress
)`
  background: none;
  border: none;
  font-family: inherit;
  font-weight: inherit;
  font-size: inherit;
  line-height: inherit;
  color: inherit;
  padding: 0;
  cursor: pointer;
  outline: 0;
  transition: all 600ms;

  &:focus {
    color: ${colors.lightYellow};
  }
`;
